import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { APP_CONSTANTS } from '../constants';
import { enqueueSnackbar } from '../packages/AppNotifier/actions';

export function useCalendlyOAuth(
  agencyId,
  initialConnected = false,
  refreshAgencyDetails,
) {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.Token.token);
  const [isConnected, setIsConnected] = useState(initialConnected);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const popupRef = useRef(null);
  const oauthStateRef = useRef(null);

  // Refs so the stable message listener always reads current values
  const agencyIdRef = useRef(agencyId);
  const tokenRef = useRef(token);
  const dispatchRef = useRef(dispatch);
  const refreshRef = useRef(refreshAgencyDetails);

  useEffect(() => {
    agencyIdRef.current = agencyId;
  }, [agencyId]);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);
  useEffect(() => {
    refreshRef.current = refreshAgencyDetails;
  }, [refreshAgencyDetails]);

  useEffect(() => {
    setIsConnected(initialConnected || false);
    setError(null);
    oauthStateRef.current = null;
  }, [agencyId]);

  useEffect(() => {
    if (initialConnected) {
      setIsConnected(true);
    }
  }, [initialConnected]);

  const openPopup = useCallback(async () => {
    if (!agencyIdRef.current) {
      setError('Agency ID is required to connect Calendly');
      return;
    }
    setIsConnecting(true);
    setError(null);

    try {
      const res = await fetch(
        `${APP_CONSTANTS.BASE_API_URL}agency/${agencyIdRef.current}/auth/calendly`,
        {
          headers: {
            Authorization: tokenRef.current,
            Accept: 'application/json',
          },
        },
      );
      if (!res.ok) throw new Error('Failed to fetch Calendly auth URL');
      const { auth_url, state } = await res.json();

      oauthStateRef.current = state;

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      popupRef.current = window.open(
        auth_url,
        'Calendly OAuth',
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      if (!popupRef.current) {
        oauthStateRef.current = null;
        throw new Error('Popup blocked. Please allow popups for this site.');
      }
    } catch (err) {
      setIsConnecting(false);
      setError(err.message);
    }
  }, []);

  const disconnect = useCallback(async () => {
    if (!agencyIdRef.current) return;
    setError(null);
    try {
      const res = await fetch(
        `${APP_CONSTANTS.BASE_API_URL}agency/${agencyIdRef.current}/auth/calendly/connection`,
        {
          method: 'DELETE',
          headers: {
            Authorization: tokenRef.current,
            Accept: 'application/json',
          },
        },
      );
      const data = await res.json();
      if (data.connected === false) {
        setIsConnected(false);
        dispatchRef.current(
          enqueueSnackbar({
            message: 'Calendly disconnected successfully',
            statusCode: 200,
          }),
        );
        if (refreshRef.current) {
          refreshRef.current();
        }
      } else {
        setError(data.message || 'Failed to disconnect Calendly');
      }
    } catch {
      setError('Failed to disconnect Calendly');
    }
  }, []);

  useEffect(() => {
    // Shared handler for both postMessage and localStorage fallback
    const processOAuthData = (data) => {
      // data: { code?, state?, error? }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close(); // optionally close
      }
      popupRef.current = null;

      const { code, state: oauthState, error: oauthError } = data;

      if (oauthError || !code) {
        setIsConnecting(false);
        setError(oauthError || 'Authorization failed');
        return;
      }

      // const state = oauthStateRef.current;
      oauthStateRef.current = null;

      (async () => {
        try {
          const res = await fetch(
            `${APP_CONSTANTS.BASE_API_URL}agency/${agencyIdRef.current}/auth/calendly/callback`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: tokenRef.current,
                Accept: 'application/json',
              },
              body: JSON.stringify({ code, state: oauthState }),
            },
          );
          const response = await res.json();
          setIsConnecting(false);
          if (response.connected === true) {
            setIsConnected(true);
            dispatchRef.current(
              enqueueSnackbar({
                message: 'Calendly connected successfully!',
                statusCode: 200,
              }),
            );
            if (refreshRef.current) {
              refreshRef.current();
            }
          } else {
            setError(response.message || 'Authorization failed');
          }
        } catch {
          setIsConnecting(false);
          setError('Failed to connect Calendly');
        }
      })();
    };

    // 1. postMessage listener (for popups with opener)
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'CALENDLY_OAUTH') return;

      const { code, state, error: oauthError } = event.data;
      processOAuthData({ code, state, error: oauthError });
    };

    // 2. localStorage listener (fallback for popups without opener)
    const handleStorage = (e) => {
      // We listen for changes on our specific key
      if (e.key === 'oauth_callback_data') {
        try {
          const raw = e.newValue;
          if (!raw) return;
          const payload = JSON.parse(raw);
          // payload = { code, state, error } (as written by popup)
          processOAuthData(payload);
          // Clean up after reading
          localStorage.removeItem('oauth_callback_data');
          localStorage.removeItem('oauth_callback_timestamp');
        } catch (err) {
          console.error('Failed to parse localStorage OAuth data', err);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, []); // dependencies unchanged (if any, adjust accordingly)

  return { isConnecting, isConnected, error, openPopup, disconnect };
}
